import { CircularProgress, Skeleton } from '@mui/material';
import classNames from 'classnames';
import { FC, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { ReactComponent as SendIcon } from 'src/assets/send-note.svg';
import { DateFormats } from 'src/constants/dateFormats';
import { addTicketNote, getTicketNotes } from 'src/store/actions/ticketActions';
import { formatDate } from 'src/utils/dateFormatter';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';

import { FormInput } from '../FormInput/FormInput';
import ShadowBox from '../ShadowBox/ShadowBox';
import classes from './TicketNotes.module.scss';

export interface TicketNotesProps {
  disabled?: boolean;
  mode?: 'view' | 'add';
  title?: string;
  emptyText?: string;
}

interface TicketNoteFormData {
  note: string;
}

const defaultValues: TicketNoteFormData = {
  note: '',
};

const TicketNotes: FC<TicketNotesProps> = ({
  disabled = false,
  mode = 'add',
  title,
  emptyText,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const loading = useLoadingSelector({ actionType: getTicketNotes.toString() });
  const loadingSend = useLoadingSelector({
    actionType: addTicketNote.toString(),
  });
  const ticket = useAppSelector((state) => state.ticket.ticketDetails);
  const ticketNotes = useAppSelector((state) => state.ticket.ticketNotes);

  useEffect(() => {
    if (ticket) {
      dispatch(getTicketNotes({ ticketId: ticket?.id }));
    }
    // eslint-disable-next-line
  }, [ticket]);

  const list = (
    <section style={mode === 'view' ? { maxHeight: 'unset' } : null}>
      {ticketNotes?.length ? (
        [...ticketNotes]
          ?.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          ?.map((note, i) => (
            <div key={i}>
              <p>{formatDate(note.createdAt, DateFormats.NOTE_DATE)}</p>
              <p>{note.text}</p>
            </div>
          ))
      ) : (
        <p>{emptyText || t('ticket_details:no_ticket_notes')}</p>
      )}
    </section>
  );

  const { control, handleSubmit, formState, reset } =
    useForm<TicketNoteFormData>({
      mode: 'onSubmit',
      defaultValues,
    });

  const onSubmit: SubmitHandler<TicketNoteFormData> = (formData) => {
    if (formData.note && ticket) {
      dispatch(addTicketNote({ ticketId: ticket?.id, text: formData.note }));
      reset(defaultValues);
    }
  };

  return (
    <ShadowBox className={classes.Container}>
      {!loading ? (
        <>
          <h5>{title || t('ticket_details:my_notes')}</h5>
          {list}
          {mode === 'add' && (
            <form
              id="ticket-note-form"
              noValidate
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormInput<TicketNoteFormData>
                control={control}
                name="note"
                disabled={disabled || loadingSend}
                placeholder={t('ticket_details:write_note')}
                inputProps={{ fullWidth: true }}
                className={classes.Input}
                endAdornment={
                  <button
                    form="ticket-note-form"
                    className={classNames(classes.SubmitButton, {
                      [classes.Enabled]: formState.isDirty && !loadingSend,
                    })}
                  >
                    {loadingSend ? (
                      <CircularProgress size="1rem" />
                    ) : (
                      <SendIcon />
                    )}
                  </button>
                }
                size="small"
              />
            </form>
          )}
        </>
      ) : (
        <div className={classes.Loading}>
          <Skeleton animation="wave" variant="text" />
          <Skeleton animation="wave" variant="rectangular" height={208} />
          <Skeleton animation="wave" variant="rectangular" height={33} />
        </div>
      )}
    </ShadowBox>
  );
};

export default TicketNotes;
